import { Injectable } from '@angular/core';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { TokenService } from '@services/token.service';
import { TokenModel } from '@data-layer/models/user';
import { TOKEN_ERROR_SKIP_URLS, TOKEN_EXCLUDE_URLS } from '@constants/setting-keys.const';
import { GoogleAnalyticsReportService } from '@services/google-analytics-report.service';
import { Store } from '@ngxs/store';
import { FetchLegalTermsAndPrivacyPolicyUpdatedDate } from '@app/shared/state';
import { RequestLoaderService } from '@services/request-loader.service';
import { enumEnvironmentName } from '@env/enumEnvironmentName';
import { environment } from '@env/environment';
import { LoggerService } from '@services/logger.service';
import { CompaniesService } from '@services/companies.service';
import { enumRespErrors } from '@data-layer/enums/common';

@Injectable({ providedIn: 'root' })
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshInProgress: boolean = false;
  private refreshTokenSubject: BehaviorSubject<TokenModel> = new BehaviorSubject<TokenModel>(null);

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private googleAnalyticsReportService: GoogleAnalyticsReportService,
    private store: Store,
    private uiService: RequestLoaderService,
    private loggerService: LoggerService,
    private companiesService: CompaniesService,
  ) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      !this.tokenService.isAuthenticated &&
      this.tokenService.getRefreshToken() &&
      !TOKEN_EXCLUDE_URLS.some((url) => request.urlWithParams.includes(url))
    ) {
      request = this.addToken(request, this.tokenService.getTokenType(), this.tokenService.getAccessToken());
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(request, next);
        } else {
          this.uiService.hideAPILoader();
          this.uiService.hideUILoader();

          if ([enumEnvironmentName.LOCAL_DEV, enumEnvironmentName.DEV, enumEnvironmentName.TEST].includes(environment.name)) {
            console.log('⚠ request handle error: ', error);
          }

          this.loggerService.logEvent(`FE_REQUEST_FAILED`, {
            route: location.pathname,
            version: localStorage.getItem('version-raw'),
            externalCompanyId: this.companiesService.companyExternalIds?.externalCompanyId,
            externalUserId: this.companiesService.userExternalId,
            acceptedTermsDate: this.companiesService.userLegalTermsAcceptedDate,
            acceptedPolicyDate: this.companiesService.userPrivacyPolicyAcceptedDate,
            error,
            timestamp: new Date(),
            isShouldSend: true,
          });

          if (
            !this.refreshInProgress &&
            ((error instanceof HttpErrorResponse && [401, 403].includes(error.status)) || error?.error?.error === 'invalid_grant') &&
            error?.error?.tag !== enumRespErrors.OrganizationFilingRestricted &&
            !TOKEN_ERROR_SKIP_URLS.some((url: string) => request.urlWithParams.includes(url)) &&
            !TOKEN_EXCLUDE_URLS.some((url) => request.urlWithParams.includes(url))
          ) {
            this.eraseToken(false);
          }

          return throwError(error);
        }
      }),
    );
  }

  private addToken(request: HttpRequest<any>, tokenType: string, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `${tokenType} ${token}`,
      },
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.refreshInProgress) {
      this.refreshInProgress = true;
      this.refreshTokenSubject.next(null);

      return this.getRefreshToken().pipe(
        switchMap((token: TokenModel) => {
          this.refreshInProgress = false;
          this.refreshTokenSubject.next(token);
          this.tokenService.setToken(token);
          this.store.dispatch(new FetchLegalTermsAndPrivacyPolicyUpdatedDate(true));
          return next.handle(this.addToken(request, token.token_type, token.access_token));
        }),
        catchError((error) => {
          if ((error instanceof HttpErrorResponse && [401, 403].includes(error.status)) || error?.error?.error === 'invalid_grant') {
            if ([enumEnvironmentName.LOCAL_DEV, enumEnvironmentName.DEV, enumEnvironmentName.TEST].includes(environment.name)) {
              console.log('⚠ getRefreshToken error: ', error);
            }

            this.eraseToken(false);
            return throwError(error);
          } else {
            return next.handle(this.addToken(request, this.tokenService.getTokenType(), this.tokenService.getAccessToken()));
          }
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((newToken) => {
          return next.handle(this.addToken(request, newToken.token_type, newToken.access_token));
        }),
        catchError((error) => {
          if ([enumEnvironmentName.LOCAL_DEV, enumEnvironmentName.DEV, enumEnvironmentName.TEST].includes(environment.name)) {
            console.log('⚠ refreshTokenSubject error: ', error);
          }

          this.uiService.hideAPILoader();
          this.uiService.hideUILoader();

          this.eraseToken(false);
          return throwError(error);
        }),
      );
    }
  }

  private eraseToken(saveUrl: boolean = true): void {
    this.tokenService.eraseToken();
    this.googleAnalyticsReportService.logout();
  }

  private getRefreshToken(): Observable<TokenModel> {
    return this.tokenService.refreshToken(this.tokenService.getRefreshToken());
  }
}
